import { useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import { polyfill } from 'smoothscroll-polyfill';
import type { AppProps } from 'next/app';
import { useCookieConsent } from '@zapier/cookies';
import { GlobalStylesProvider } from '@zapier/design-system';
import { builder } from '@builder.io/react';

import { GlobalStyles } from '../components/GlobalStyles';
import { WistiaVideoProvider } from '../components/WistiaVideo/WistiaVideo.context';
import { ZapierClient } from '../graphql/clients';
import { isDatadog } from '../utils/isDatadog';
import {
  TrackRouting,
  TrackingProvider,
  initializeDatadogRum,
  initializeSentry,
  nextJsReportWebVitals,
} from '../observability';
import {
  startSessionReplay,
  stopSessionReplay,
} from '../observability/datadogRum';

builder.init(process.env.NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY!);

// Don't initialize Sentry for Datadog traffic
const shouldInitSentry = !isDatadog();
if (shouldInitSentry) {
  initializeSentry({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || '',
    commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA || '',
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT || '',
  });
}

initializeDatadogRum();

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export const reportWebVitals = nextJsReportWebVitals;

function App({ Component, pageProps }: AppProps) {
  useEffect(() => {
    polyfill();
  }, []);

  const cookieConsent = useCookieConsent();

  useEffect(() => {
    if (cookieConsent.byName.analytics) {
      startSessionReplay();
    } else {
      stopSessionReplay();
    }
  }, [cookieConsent.byName.analytics]);

  return (
    <GlobalStylesProvider isPlatform={true} isRebrand={true}>
      <ApolloProvider client={ZapierClient}>
        <WistiaVideoProvider>
          <TrackingProvider>
            <TrackRouting />
            <GlobalStyles />
            <Component {...pageProps} />
          </TrackingProvider>
        </WistiaVideoProvider>
      </ApolloProvider>
    </GlobalStylesProvider>
  );
}

export default App;
